import { API } from "../utils/amplifyWrapper";
import { toast } from 'react-toastify';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Play, Search, Plus } from 'lucide-react';

import { Modal, ModalHeader, ModalTitle, ModalBody } from './ui/Modal';
import Spinner from './Spinner';
import encodeQueryData from '../utils/Query';

import AudioPlayer from './AudioPlayer';
import PodcastEpisodeSettings from './PodcastEpisodeSettings';

function AddAudioToPodcastModal({ show, onHide, orgId, integrationId, podcastId, showDetails, onSuccess }) {

    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [showEpisodeSettings, setShowEpisodeSettings] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState(null);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [audio, setAudio] = useState(null);
    const [audioTitle, setAudioTitle] = useState("Press play to listen to this.");

    const getContents = (nextPageToken = null) => {
        let url = `/organisation/${orgId}/integration/${integrationId}/content`;

        let params = {};

        if (nextPageToken) {

            params["next_page_token"] = nextPageToken;

        }

        if (Object.keys(params).length > 0) {

            url = url + "?" + encodeQueryData(params);

        }

        return API.get(`main`, url);
    };

    const getNextPage = () => {

        setIsFetchingMore(true);

        getContents(nextPageToken).then((response) => {
            setContents([...contents, ...response.data]);
            setIsFetchingMore(false);
            if (response.hasOwnProperty("next_page_token")) {
                setNextPageToken(response.next_page_token)
            }
        });
    };

    const searchContent = (keyword) => {
        API.get(`main`, `/organisation/${orgId}/integration/${integrationId}/search?keyword=${keyword}`, { headers: { "Content-Type": "application/json" } }).then((response) => {
            setSearchResults(response.data.hits.hits);
        });
    }

    useEffect(() => {

        getContents()
            .then(response => {
                setContents(response.data);
                if (response.hasOwnProperty("next_page_token")) {
                    setNextPageToken(response.next_page_token)
                }
            })
            .catch(error => {
                toast.error("Something went wrong!");
            })
            .finally(() => setLoading(false));

    }, []);

    const SearchResults = () => {
        return (
            <React.Fragment>
                <div className="content-filter">
                    <span className="me-2">Showing search results for <strong>{searchKeyword}</strong></span>
                    <span className="clear-content-filter" onClick={() => { setSearchKeyword(""); setSearchResults(null) }}>Clear</span>
                </div>
                {
                    searchResults.length > 0 ? (
                        <div className="list-group list-group-flush">
                            {
                                searchResults.map((item, index) => {
                                    return (
                                        <li 
                                            className="list-group-item border rounded-3 mb-3 p-3"
                                            style={{
                                                transition: 'all 0.2s ease',
                                                cursor: 'default'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--bs-light-bg-subtle)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '';
                                                e.currentTarget.style.boxShadow = '';
                                                e.currentTarget.style.transform = '';
                                            }}
                                            key={`content-${index}`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center gap-3">
                                                <div className="flex-fill overflow-hidden">
                                                    <h4 className="mb-2 text-truncate fs-5 fw-bold text-dark">
                                                        {item._source.title.S}
                                                    </h4>
                                                    <div className="d-flex align-items-center flex-wrap gap-2 small text-muted">
                                                        {
                                                            (null !== item._source.published_at.S)
                                                            && (
                                                                <span className="fw-medium">
                                                                    Published on {moment(item._source.published_at.S).format("MMM D, YYYY")}
                                                                </span>
                                                            )
                                                        }
                                                        {
                                                            item._source.audio_duration && (
                                                                <>
                                                                    {item._source.published_at.S && <span>•</span>}
                                                                    <span className="fw-medium">
                                                                        {moment.duration(item._source.audio_duration.S, 'seconds').minutes()} min listen
                                                                    </span>
                                                                </>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2 align-items-center flex-shrink-0">
                                                    {
                                                        item._source.published_at.S
                                                        && (
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAudio(item._source.full_audio_url.S);
                                                                    setAudioTitle(item._source.title.S);
                                                                }}
                                                                className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-3"
                                                                title="Play audio"
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                                                                    e.currentTarget.style.transform = 'scale(1.05)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '';
                                                                    e.currentTarget.style.transform = '';
                                                                }}
                                                                style={{ transition: 'all 0.2s ease' }}
                                                            >
                                                                <Play size={20} />
                                                            </button>
                                                        )
                                                    }
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedEpisode({
                                                                ...item,
                                                                content_id: item._source.SK.S.split("#")[1],
                                                                author: showDetails.author,
                                                                category: showDetails.category,
                                                                is_published: true
                                                            });
                                                            onHide();
                                                            setShowEpisodeSettings(true);
                                                        }}
                                                        className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-3 fw-semibold shadow-sm"
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.25)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = '';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }}
                                                        style={{ transition: 'all 0.2s ease' }}
                                                    >
                                                        <Plus size={18} />
                                                        <span>Add</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    )
                                })
                            }
                        </div>
                    ) :
                        (
                            <p style={{ marginTop: '32px' }}>No content found in this keyword. Try a different keyword.</p>
                        )
                }
            </React.Fragment>
        )
    }

    const Contents = () => {
        return (
            <React.Fragment>
                <div className="list-group list-group-flush">
                    {
                        contents.map((item, index) => {
                            return (
                                <li 
                                    className="list-group-item border rounded-3 mb-3 p-3"
                                    style={{
                                        transition: 'all 0.2s ease',
                                        cursor: 'default'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--bs-light-bg-subtle)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '';
                                        e.currentTarget.style.boxShadow = '';
                                        e.currentTarget.style.transform = '';
                                    }}
                                    key={`content-${index}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                        <div className="flex-fill overflow-hidden">
                                            <h4 className="mb-2 text-truncate fs-5 fw-bold text-dark">
                                                {item.title}
                                            </h4>
                                            <div className="d-flex align-items-center flex-wrap gap-2 small text-muted">
                                                {
                                                    (null !== item.published_at)
                                                    && (
                                                        <span className="fw-medium">
                                                            Published on {moment(item.published_at).format("MMM D, YYYY")}
                                                        </span>
                                                    )
                                                }
                                                {
                                                    item.audio_duration && (
                                                        <>
                                                            {item.published_at && <span>•</span>}
                                                            <span className="fw-medium">
                                                                {moment.duration(item.audio_duration, 'seconds').minutes()} min listen
                                                            </span>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2 align-items-center flex-shrink-0">
                                            {
                                                item.published_at
                                                && (
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setAudio(item.full_audio_url);
                                                            setAudioTitle(item.title);
                                                        }}
                                                        className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-3"
                                                        title="Play audio"
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = '';
                                                            e.currentTarget.style.transform = '';
                                                        }}
                                                        style={{ transition: 'all 0.2s ease' }}
                                                    >
                                                        <Play size={20} />
                                                    </button>
                                                )
                                            }
                                            <button 
                                                onClick={() => {
                                                    setSelectedEpisode({
                                                        ...item,
                                                        content_id: item.SK.split("#")[1],
                                                        author: showDetails.author,
                                                        category: showDetails.category,
                                                        is_published: true
                                                    });
                                                    onHide();
                                                    setShowEpisodeSettings(true);
                                                }}
                                                className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-3 fw-semibold shadow-sm"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.25)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = '';
                                                    e.currentTarget.style.boxShadow = '';
                                                }}
                                                style={{ transition: 'all 0.2s ease' }}
                                            >
                                                <Plus size={18} />
                                                <span>Add</span>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            )
                        })
                    }
                </div>
                {
                    (isFetchingMore) ?
                        (
                            <button className={classNames("btn--fetch-more", "mt-3")}>
                                <Spinner color="var(--bs-secondary-color)" />
                            </button>
                        )
                        :
                        (
                            <button className={classNames("btn--fetch-more", "mt-3", "mb-5", { "d-none": null == nextPageToken })} onClick={() => { getNextPage() }}>Fetch More</button>
                        )
                }
            </React.Fragment>
        )
    }

    return (
        <>
            <PodcastEpisodeSettings
                show={showEpisodeSettings}
                onHide={() => setShowEpisodeSettings(false)}
                orgId={orgId}
                integrationId={integrationId}
                podcastId={podcastId}
                selectedEpisode={selectedEpisode}
                isNew={true}
                onSuccess={onSuccess}
            />

            <Modal 
                isOpen={show} 
                onClose={onHide}
                size="xl"
                className="modal-add-content"
            >
                <ModalHeader closeButton>
                    <ModalTitle>Add Audios</ModalTitle>
                </ModalHeader>
                <ModalBody className="container">
                    <div className="content-search">
                        <div className="input-group input-group--search">
                            <span className="input-group-text">
                                <Search size={18} />
                            </span>
                            <input type="text" className="form-control" placeholder="Search" value={searchKeyword} onChange={(event) => { setSearchKeyword(event.target.value) }} onKeyDown={(event) => { if ("Enter" === event.key) { searchContent(event.target.value) } }} />
                        </div>
                    </div>
                    {
                        loading
                            ? <></>
                            : (searchResults)
                                ? (
                                    <SearchResults />
                                )
                                : <Contents />
                    }
                </ModalBody>
            </Modal>
            {
                (audio) &&
                <div className="audio-player-wrapper">
                    <AudioPlayer audio={audio} title={audioTitle} />
                </div>
            }
        </>
    );
}

export default AddAudioToPodcastModal;
